import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from '../../App';
import { Building2, Plus, Search, Edit, Trash, Eye, X, Loader2, MoreVertical, RefreshCw
} from 'lucide-react';
import FormBuilder from './forms/FormBuilder.jsx';
import { empresaSchema } from './forms/schemas/empresaSchema.js';

const DropdownMenu = ({ item, onEdit, onView, onDelete, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  //const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  
  const handleToggle = (e) => {
    if (!isOpen) {
      const rect = e.target.getBoundingClientRect();
      const menuHeight = 120; // Alto aproximado del menú
      const menuWidth = 140;  // Ancho del menú
    
      let top = rect.bottom + 8;
      let left = rect.right - menuWidth;
    
      // Si se sale por abajo, ponerlo arriba
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 8;
      }
    
      // Si se sale por la derecha, ajustar
      if (left < 0) {
        left = rect.left;
      }
    
      setMenuPosition({ top, left });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        title="Acciones"
      >
        <MoreVertical size={16} />
      </button>
      
      {isOpen && (
        <>
         <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          <div className={`fixed z-50 rounded-lg shadow-lg py-1 min-w-[140px] ${
            isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'
          }`}
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}>
            <button
              onClick={() => {
                onView(item);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center ${
              isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Eye size={14} className="mr-2" />
              Ver detalles
            </button>
            
            <button
              onClick={() => {
                onEdit(item);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center ${
              isDarkMode ? 'text-blue-400 hover:bg-gray-600' : 'text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Edit size={14} className="mr-2" />
              Editar
            </button>
            
            <button
              onClick={() => {
                onDelete(item);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left flex items-center ${
              isDarkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <Trash size={14} className="mr-2" />
              Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AdminEmpresas = () => {
  const { isDarkMode } = useTheme();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  }, []);
  
  useEffect(() => {
    if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
      const inputElement = searchInputRef.current;
      const currentSelectionStart = inputElement.selectionStart;
      const currentSelectionEnd = inputElement.selectionEnd;
      setTimeout(() => {
        if (inputElement && inputElement.parentNode) {
          inputElement.focus();
          inputElement.setSelectionRange(currentSelectionStart, currentSelectionEnd);
        }
      }, 0);
    }
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const response = await fetch('/api/empresas', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        const empresasNormalizadas = data.data.empresas.map(empresa => ({
          ...empresa,
          id: empresa.idsucutien,
          activo: empresa.activo ? 1 : 0,
          fecha_creacion: new Date(empresa.fecalta).toLocaleDateString('es-ES'),
          ubicacion_completa: `${empresa.ubicacion?.municipio?.trim() || ''}, ${empresa.ubicacion?.estado?.trim() || ''}, ${empresa.ubicacion?.pais?.trim() || ''}`.replace(/^,\s*|,\s*$/g, ''),
          pais_nombre: empresa.ubicacion?.pais?.trim() || '',
          estado_nombre: empresa.ubicacion?.estado?.trim() || '',
          municipio_nombre: empresa.ubicacion?.municipio?.trim() || ''
        }));
        setEmpresas(empresasNormalizadas);
      } else {
          console.error('❌ Error API empresas:', data);          
      }
    } catch (err) {
      console.error('❌ Error cargando empresas:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (!searchTerm) return empresas;
    return empresas.filter(item => 
      Object.values(item).some(value => {
        if (value === null || value === undefined) {
          return false;
        }
        const stringValue = String(value).toLowerCase();
        const searchTermLower = searchTerm.toLowerCase();      
        return stringValue.includes(searchTermLower);
      })
    );
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleView = (item) => {
    setModalMode('view');
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setModalMode('delete');
    setSelectedItem(item);
    setShowModal(true);	
  };

  const handleSave = async (formData) => {
  const token = sessionStorage.getItem('mapasac_access_token');
  
  try {
    let url, method;
    
    if (modalMode === 'create') {
      url = '/api/empresas';
      method = 'POST';
    } else if (modalMode === 'edit') {
      url = `/api/empresas/${selectedItem?.idsucutien}`;
      method = 'PUT';
    } else if (modalMode === 'delete') {
      url = `/api/empresas/${selectedItem?.idsucutien}`;
      method = 'DELETE';
    }
    
    const response = await fetch(url, {
      method: method,
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: modalMode !== 'delete' ? JSON.stringify(formData) : undefined
    });
    
    const data = await response.json();
    
    if (data.success) {
      if (modalMode === 'create') {
        toast.success('Empresa creada correctamente');
      } else if (modalMode === 'edit') {
        toast.success('Empresa actualizada correctamente');
      } else if (modalMode === 'delete') {
        toast.success('Empresa eliminada correctamente');
      }
      setShowModal(false);
      setTimeout(() => {
        loadEmpresas();
      }, 100);
    } else {
      throw new Error(data.error?.message || data.error || 'Error en la operación');
    }
    
  } catch (error) {
    console.error('Error en handleSave:', error);
    throw error;
  }
  };

  const getStatusBadge = (status) => {
    if (status === 1 || status === 'conectado') {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Activo</span>;
    }
    return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">Inactivo</span>;
  };

  const handleActualizar = () => {
    setSearchTerm('');
    setSearchInput('');
    loadEmpresas();
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  // Toolbar Component
  const Toolbar = () => (
  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4 flex-1">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchInput}
            onChange={handleSearchChange}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
            }`} 
            placeholder="Buscar en empresas..."
            style={{ fontSize: '16px' }}
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('');
                setSearchTerm('');
                if (searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Limpiar búsqueda"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-3">
        <button 
          onClick={handleActualizar}
          className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
          isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'
          }`}
          title="Actualizar datos y limpiar filtros"
        >
          <RefreshCw size={16} />
          <span>Actualizar</span>
        </button>
        <button 
          onClick={handleCreate}
          className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
          isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-700' : 'text-gray-600 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Plus size={16} />
          <span>Crear</span>
        </button>
      </div>
    </div>
  </div>
);

  // Empresas Table Component
  const EmpresasTable = () => {
  const data = getFilteredData();
  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} overflow-hidden`}>
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh / var(--ui-escala, 1) - 270px)' }}> 
        <table className="min-w-full divide-y divide-gray-200">
          <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} sticky top-0 z-10`}>
            <tr>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Empresa</th>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Sucursal</th>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Ubicación</th>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Email</th>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Estado</th>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Fecha Alta</th>
              <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((empresa, index) => (
              <tr key={empresa.id} className={`${ isDarkMode 
                  ? `hover:bg-gray-700 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}` 
                  : `hover:bg-blue-50 ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`
                  }`}>
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <Building2 className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{empresa.descripcio}</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {empresa.idsucutien}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                    No. {empresa.nosucursal}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{empresa.municipio_nombre}</div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{empresa.estado_nombre}, {empresa.pais_nombre}</div>
                </td>
                <td className={`px-4 py-2 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  {empresa.email}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {getStatusBadge(empresa.activo)}
                </td>
                <td className={`px-4 py-2 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  {empresa.fecha_creacion}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end">
                    <DropdownMenu 
                      item={empresa}
                      onEdit={handleEdit}
                      onView={handleView}
                      onDelete={handleDelete}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Toolbar />
      <div className="flex-1 p-6 pb-2">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin" size={32} />
            <span className="ml-2 text-gray-600">Cargando empresas...</span>
          </div>
        ) : (
          <EmpresasTable />
        )}
      </div>      
      <div className="px-6 py-4">
        <div className={`flex items-center justify-between text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>Mostrando {getFilteredData().length} de {empresas.length} registros</span>
          <div className={`flex items-center space-x-2 font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            <span>💪 Powered by MAPASAC IT SYSTEMS</span>
          </div>
          <span>Última actualización: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>
      {showModal && (
        <FormBuilder
          schema={empresaSchema}
          mode={modalMode}
          initialData={selectedItem}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          isDarkMode={isDarkMode}
          title="Empresa"
          apiEndpoint="/api/empresas"
        />
      )}      
    </div>
  );
};

export default AdminEmpresas;